import "react-day-picker/lib/style.css";

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import DayPicker from "react-day-picker";
import { Form, Icon } from "semantic-ui-react";
import styled from "styled-components";

const Wrap = styled.div`
  position: relative;
`;

const CalendarPopup = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const VALID_DATE = /^\d{4}-\d{2}-\d{2}$/;

const clamp = (raw) => {
  let result = raw;
  if (result.length >= 7 && result[4] === "-") {
    const mm = parseInt(result.slice(5, 7), 10);
    if (mm > 12) result = result.slice(0, 5) + "12" + result.slice(7);
    else if (mm < 1) result = result.slice(0, 5) + "01" + result.slice(7);
  }
  if (result.length >= 10 && result[4] === "-" && result[7] === "-") {
    const dd = parseInt(result.slice(8, 10), 10);
    const maxDay = dayjs(result.slice(0, 7) + "-01").daysInMonth();
    if (dd > maxDay)
      result = result.slice(0, 8) + String(maxDay).padStart(2, "0");
    else if (dd < 1) result = result.slice(0, 8) + "01";
  }
  return result;
};

export const CalendarInput = ({
  name,
  value: propValue,
  onChange,
  label,
  fromMonth,
  toMonth,
}) => {
  const [text, setText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    setText(propValue || "");
  }, [propValue]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const commit = (newValue) => {
    onChange({ target: { name, value: newValue } });
  };

  const onCalendarClick = (date) => {
    const newValue = date ? dayjs(date).format("YYYY-MM-DD") : "";
    setText(newValue);
    setIsOpen(false);
    commit(newValue);
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    onCalendarClick(null);
  };

  const handleChange = (e) => {
    const raw = clamp(e.target.value);
    setText(raw);
    if (VALID_DATE.test(raw) && dayjs(raw).isValid()) {
      commit(raw);
    } else if (raw === "") {
      commit("");
    }
  };

  const handleBlur = (e) => {
    if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
    if (!VALID_DATE.test(text) || !dayjs(text).isValid()) {
      setText(propValue || "");
    }
  };

  const parsedDate =
    VALID_DATE.test(text) && dayjs(text).isValid()
      ? dayjs(text).toDate()
      : null;

  const calendarMonth = (() => {
    if (parsedDate) return parsedDate;
    const partial = dayjs(
      text.length >= 7 ? text.slice(0, 7) + "-01" : text + "-01-01"
    );
    return partial.isValid() && text.length >= 4
      ? partial.toDate()
      : new Date();
  })();

  return (
    <Wrap ref={wrapRef}>
      <Form.Input
        label={label}
        value={text}
        name={name}
        autoComplete="off"
        onChange={handleChange}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Tab") setIsOpen(false);
        }}
        onClick={() => setIsOpen(true)}
        icon={
          text ? (
            <Icon name="close" link onClick={handleIconClick} />
          ) : (
            <Icon name="calendar" />
          )
        }
      />
      {isOpen && (
        <CalendarPopup>
          <DayPicker
            onDayClick={onCalendarClick}
            month={calendarMonth}
            selectedDays={parsedDate}
            fromMonth={fromMonth}
            toMonth={toMonth}
          />
        </CalendarPopup>
      )}
    </Wrap>
  );
};

CalendarInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  fromMonth: PropTypes.instanceOf(Date),
  toMonth: PropTypes.instanceOf(Date),
};
